import { ScatterplotLayer } from '@deck.gl/layers';
import type { Scene, ClusterData, ClusterSpecies, PlotSimData, MapAdapter } from '../types';
import { RadialChart } from '../components/RadialChart';
import { clusterColorById } from '../clusterPalette';
import { hexToRgba } from '../colorUtils';

const NS = 'http://www.w3.org/2000/svg';

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(NS, tag);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function dot64(a: number[], b: number[]): number {
  let s = 0;
  for (let i = 0; i < 64; i++) s += a[i] * b[i];
  return s;
}

interface PlotDatum {
  position: [number, number];
  color: [number, number, number, number];
  domId: number;
}

interface EnrichedSpecies extends ClusterSpecies {
  clusterId:  number;
  color:      string;
  ownSim:     number;   // cosine sim to own centroid
  confidence: number;   // normalized [0, 1] across all species
}

export class SceneGroups implements Scene {
  readonly panel: HTMLElement;
  private radial!:    RadialChart;
  private layer: ScatterplotLayer<PlotDatum> | null = null;
  private plotData: PlotDatum[] = [];
  // plotSims filtered to study bbox (removes GPS outliers)
  private validPlots:  PlotSimData[] = [];
  // Dominant cluster id per valid plot (argmax of sims[])
  private plotDomCluster: number[] = [];
  private built      = false;
  private selectedId = 0;

  private readonly enriched: EnrichedSpecies[] = [];
  private dotEls:      SVGCircleElement[] = [];
  private baseOpacity: number[] = [];
  // Classification accuracy per cluster (fraction of species most similar to own centroid)
  private classAcc: Map<number, number> = new Map();

  // SVG zoom state
  private vb = { x: 0, y: 0, w: 400, h: 370 };
  private scatterSvg!: SVGSVGElement;

  constructor(
    root: HTMLElement,
    private readonly clusters: ClusterData[],
    private readonly plotSims: PlotSimData[],
    private readonly map: MapAdapter,
  ) {
    // ── Enrich species with confidence ────────────────────────────────────────
    const centroids = clusters.map(c => c.centroid_embed);

    const raw: { sp: ClusterSpecies; clusterId: number; rawConf: number; ownSim: number }[] = [];
    clusters.forEach(c => {
      c.species.forEach(sp => {
        const sims     = centroids.map(ct => dot64(sp.embed, ct));
        const ownSim   = sims[c.id];
        const bestOther = Math.max(...sims.filter((_, i) => i !== c.id));
        raw.push({ sp, clusterId: c.id, rawConf: ownSim - bestOther, ownSim });
      });
    });

    const confs  = raw.map(r => r.rawConf);
    const minC   = Math.min(...confs);
    const rangeC = Math.max(...confs) - minC || 1;

    raw.forEach(r => {
      this.enriched.push({
        ...r.sp,
        clusterId:  r.clusterId,
        color:      clusterColorById(r.clusterId),
        ownSim:     r.ownSim,
        confidence: (r.rawConf - minC) / rangeC,
      });
    });

    // Classification accuracy: % species in each cluster most similar to own centroid
    clusters.forEach(c => {
      const sps     = this.enriched.filter(e => e.clusterId === c.id);
      const correct = sps.filter(e => {
        const bestId = centroids.map((ct, j) => ({ j, s: dot64(e.embed, ct) }))
          .sort((a, b) => b.s - a.s)[0].j;
        return bestId === c.id;
      }).length;
      this.classAcc.set(c.id, sps.length > 0 ? correct / sps.length : 0);
    });

    // ── Panel HTML ────────────────────────────────────────────────────────────
    this.panel = document.createElement('div');
    this.panel.className = 'sm-panel';
    this.panel.dataset['scene'] = 'groups';
    this.panel.innerHTML = `
      <div class="sm-panel-header">
        <div class="sm-panel-title">Our Vegetation Classes</div>
        <div class="sm-panel-sub">116 species · 8 habitat groups · embedding feature space</div>
      </div>
      <div class="sm-grp-narrative">
        We use the field team's vegetation labels — recorded by surveyors as they walked each
        plot — and compare them to what our model extracts from satellite data. This helps us
        determine if our approaches from space agree with the field labels — and answer:
        "Are these categories ecologically meaningful and useful for detecting future change?"
      </div>
      <div class="sm-headline">
        The satellite model and field surveyors agree: these eight vegetation groups
        are ecologically real and detectable from space — a robust foundation for
        measuring future habitat change.
      </div>
      <div class="sm-grp-description">
        <strong>What this scene shows:</strong> Each dot is a plant species placed by
        satellite-detected habitat similarity. Colour = field surveyor's vegetation group.
        When dots of the same colour cluster together the satellite model and the field
        labels agree — the groups are ecologically real. The <em>% aligned</em> figure
        shows how often the model would pick the same label a surveyor did.
      </div>
      <div class="sm-grp-body">

        <!-- Left: scatter -->
        <div class="sm-grp-left" id="sm-grp-left">
          <div class="sm-grp-scatter-header">
            <span class="sm-section-label" style="margin:0;">Feature Space
              <button class="sm-grp-info-btn" id="sm-grp-info-btn" title="What is this chart?">ℹ</button>
            </span>
            <div class="sm-grp-scatter-controls">
              <button class="sm-grp-zoom-btn" id="sm-grp-zoom-in"  title="Zoom in">+</button>
              <button class="sm-grp-zoom-btn" id="sm-grp-zoom-out" title="Zoom out">−</button>
              <button class="sm-grp-zoom-btn" id="sm-grp-zoom-reset" title="Reset zoom">⊡</button>
              <button class="sm-grp-zoom-btn" id="sm-grp-fullscreen" title="Full screen">⛶</button>
            </div>
          </div>
          <div id="sm-grp-info-tooltip" class="sm-grp-info-tooltip" style="display:none;">
            Each dot is a plant species. Species placed close together share similar
            satellite-detected habitats — the model learned this from imagery alone,
            without being told which plants grow where.<br>
            <span style="opacity:0.7;">Colour = vegetation group assigned by field surveyors.</span>
          </div>
          <div id="sm-grp-svg-wrap" style="position:relative;cursor:crosshair;"></div>
        </div>

        <!-- Right: controls + legend + detail -->
        <div class="sm-grp-right">

          <!-- Confidence slider -->
          <div class="sm-grp-conf-section">
            <div class="sm-grp-conf-label">
              <span>Show all species</span>
              <span>High confidence only</span>
            </div>
            <input type="range" class="sm-grp-conf-slider" id="sm-grp-conf-slider"
                   min="0" max="100" value="0" step="1">
          </div>

          <!-- Legend -->
          <div class="sm-grp-legend" id="sm-grp-legend"></div>

          <!-- GPF validation note -->
          <div class="sm-grp-validation">
            <strong>The surveying work and the satellite model are in agreement.</strong>
            The naming convention aligns with the species collection and habitat
            distributions detected by the GPF models.
          </div>

          <!-- Selected cluster detail -->
          <div class="sm-grp-detail" id="sm-grp-detail">
            <div class="sm-grp-detail-name" id="sm-grp-detail-name">—</div>
            <div class="sm-grp-detail-meta" id="sm-grp-detail-meta"></div>
            <div class="sm-section-label" style="margin-top:8px;">Embedding Fingerprint</div>
            <div id="sm-grp-radial-wrap"></div>
            <div class="sm-section-label" style="margin-top:8px;">Species by Alignment</div>
            <div class="sm-grp-sp-list" id="sm-grp-sp-list"></div>
          </div>
        </div>
      </div>`;
    root.appendChild(this.panel);
  }

  onEnter(): void {
    if (!this.built) {
      this.build();
      this.built = true;
    }
    this.map.addLayer(this.layer!);
    this.selectGroup(this.selectedId);
  }

  onLeave(): void {
    this.radial?.stop();
    if (this.layer) this.map.removeLayer(this.layer);
  }

  // ── Build ──────────────────────────────────────────────────────────────────

  private build(): void {
    this.buildScatter();
    this.buildLegend();
    this.buildSlider();
    this.buildInfoTooltip();
    this.buildZoomControls();
    this.buildFullscreen();
    this.buildMainMapMarkers();

    const radialWrap = this.panel.querySelector<HTMLElement>('#sm-grp-radial-wrap')!;
    this.radial = new RadialChart(radialWrap, 220, 150);
  }

  // ── UMAP Scatter ──────────────────────────────────────────────────────────

  private buildScatter(): void {
    const wrap = this.panel.querySelector<HTMLElement>('#sm-grp-svg-wrap')!;
    const W = 400, H = 370, pad = 24;
    this.vb = { x: 0, y: 0, w: W, h: H };

    const uxs = this.enriched.map(e => e.ux);
    const uys = this.enriched.map(e => e.uy);
    const x0  = Math.min(...uxs), x1 = Math.max(...uxs);
    const y0  = Math.min(...uys), y1 = Math.max(...uys);
    const sc  = Math.min((W - pad * 2) / (x1 - x0), (H - pad * 2) / (y1 - y0));
    const ox  = pad + (W - pad * 2 - (x1 - x0) * sc) / 2;
    const oy  = pad + (H - pad * 2 - (y1 - y0) * sc) / 2;
    const px  = (ux: number) => ox + (ux - x0) * sc;
    const py  = (uy: number) => oy + (uy - y0) * sc;

    const centroidPos = this.clusters.map(c => {
      const sps = this.enriched.filter(e => e.clusterId === c.id);
      return {
        x: sps.reduce((s, e) => s + e.ux, 0) / sps.length,
        y: sps.reduce((s, e) => s + e.uy, 0) / sps.length,
      };
    });

    const maxCount = Math.max(...this.enriched.map(e => e.count), 1);

    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', `${this.vb.x} ${this.vb.y} ${this.vb.w} ${this.vb.h}`);
    svg.setAttribute('width',  String(W));
    svg.setAttribute('height', String(H));
    svg.style.cssText = 'display:block;';
    this.scatterSvg = svg;

    // Defs
    const defs = document.createElementNS(NS, 'defs');
    defs.innerHTML = `
      <filter id="grp-g1" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="1.5" result="b"/>
        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
      <filter id="grp-g2" x="-80%" y="-80%" width="260%" height="260%">
        <feGaussianBlur stdDeviation="3" result="b"/>
        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>`;
    svg.appendChild(defs);

    // Grid
    const grid = document.createElementNS(NS, 'g');
    for (let x = 0; x <= W; x += 40) grid.appendChild(svgEl('line', { x1: String(x), y1: '0', x2: String(x), y2: String(H), stroke: 'rgba(151,184,203,.04)', 'stroke-width': '1' }));
    for (let y = 0; y <= H; y += 40) grid.appendChild(svgEl('line', { x1: '0', y1: String(y), x2: String(W), y2: String(y), stroke: 'rgba(151,184,203,.04)', 'stroke-width': '1' }));
    svg.appendChild(grid);

    // Tooltip div
    const tip = document.createElement('div');
    tip.className = 'sm-grp-scatter-tooltip';
    wrap.appendChild(tip);

    // Species dots
    this.dotEls     = [];
    this.baseOpacity = [];

    this.enriched.forEach((e, i) => {
      const r  = 3 + Math.sqrt(e.count / maxCount) * 6;
      const op = 0.15 + e.confidence * 0.80;
      this.baseOpacity.push(op);

      const circle = svgEl('circle', {
        cx: String(Math.round(px(e.ux) * 10) / 10),
        cy: String(Math.round(py(e.uy) * 10) / 10),
        r:  String(r),
        fill:    e.color,
        opacity: String(op),
        filter:  'url(#grp-g1)',
      });
      circle.style.cursor = 'pointer';

      circle.addEventListener('mouseenter', ev => {
        circle.setAttribute('filter',  'url(#grp-g2)');
        circle.setAttribute('opacity', '1');
        const rect    = wrap.getBoundingClientRect();
        const svgRect = svg.getBoundingClientRect();
        tip.style.left    = `${svgRect.left - rect.left + parseFloat(circle.getAttribute('cx')!) + 10}px`;
        tip.style.top     = `${svgRect.top  - rect.top  + parseFloat(circle.getAttribute('cy')!) - 10}px`;
        tip.style.display = 'block';
        tip.innerHTML = `${e.name}<br><span style="color:${e.color}">${this.clusters[e.clusterId].label}</span>
          <span style="color:var(--c-grey)"> · ${Math.round(e.confidence * 100)}% aligned</span>`;
        (ev as MouseEvent).stopPropagation();
      });

      circle.addEventListener('mouseleave', () => {
        tip.style.display = 'none';
        this.restoreDotOpacity(i);
        circle.setAttribute('filter', 'url(#grp-g1)');
      });

      circle.addEventListener('click', () => this.selectGroup(e.clusterId));

      svg.appendChild(circle);
      this.dotEls.push(circle);
    });

    // Centroid diamonds per cluster
    this.clusters.forEach(c => {
      const col = clusterColorById(c.id);
      const cp  = centroidPos[c.id];
      const cx  = px(cp.x), cy = py(cp.y);
      const s   = 6;

      const diamond = svgEl('path', {
        d: `M ${cx} ${cy - s} L ${cx + s} ${cy} L ${cx} ${cy + s} L ${cx - s} ${cy} Z`,
        fill:    col,
        opacity: '0.95',
        filter:  'url(#grp-g2)',
        stroke:  'rgba(255,255,255,0.3)',
        'stroke-width': '0.8',
      });
      diamond.style.cursor = 'pointer';
      diamond.addEventListener('click',      () => this.selectGroup(c.id));
      diamond.addEventListener('mouseenter', () => {
        tip.style.display = 'block';
        const rect    = wrap.getBoundingClientRect();
        const svgRect = svg.getBoundingClientRect();
        tip.style.left = `${svgRect.left - rect.left + cx + 10}px`;
        tip.style.top  = `${svgRect.top  - rect.top  + cy - 10}px`;
        const acc = Math.round((this.classAcc.get(c.id) ?? 0) * 100);
        tip.innerHTML = `<span style="color:${col}">${c.label}</span><br>
          <span style="color:var(--c-grey)">centroid · ${acc}% of species most aligned here</span>`;
      });
      diamond.addEventListener('mouseleave', () => { tip.style.display = 'none'; });
      svg.appendChild(diamond);

      // Genus label
      const genusWord = c.label.split(' ')[0];
      const lbl = svgEl('text', {
        x: String(cx + s + 5), y: String(cy + 4),
        fill: col, 'font-size': '8', 'font-family': 'Courier New,monospace',
        opacity: '0.75', 'letter-spacing': '0.5',
      });
      lbl.textContent = genusWord;
      svg.appendChild(lbl);
    });

    // Wheel zoom
    wrap.addEventListener('wheel', ev => {
      ev.preventDefault();
      const factor = ev.deltaY > 0 ? 1.15 : 0.87;
      const rect   = wrap.getBoundingClientRect();
      const mx     = (ev.clientX - rect.left) / W * this.vb.w + this.vb.x;
      const my     = (ev.clientY - rect.top)  / H * this.vb.h + this.vb.y;
      this.vb.x = mx + (this.vb.x - mx) * factor;
      this.vb.y = my + (this.vb.y - my) * factor;
      this.vb.w *= factor;
      this.vb.h *= factor;
      this.applyViewBox();
    }, { passive: false });

    wrap.appendChild(svg);
  }

  private applyViewBox(): void {
    this.scatterSvg.setAttribute('viewBox',
      `${this.vb.x} ${this.vb.y} ${this.vb.w} ${this.vb.h}`);
  }

  private restoreDotOpacity(i: number): void {
    const e   = this.enriched[i];
    const pct = parseInt((this.panel.querySelector<HTMLInputElement>('#sm-grp-conf-slider')!).value) / 100;
    const isSelected = e.clusterId === this.selectedId;
    if (e.confidence < pct) {
      this.dotEls[i].setAttribute('opacity', '0.04');
    } else if (isSelected) {
      this.dotEls[i].setAttribute('opacity', String(Math.max(0.55, this.baseOpacity[i])));
    } else {
      this.dotEls[i].setAttribute('opacity', String(this.baseOpacity[i]));
    }
  }

  // ── Legend ────────────────────────────────────────────────────────────────

  private buildLegend(): void {
    const el = this.panel.querySelector<HTMLElement>('#sm-grp-legend')!;
    this.clusters.forEach(c => {
      const col = clusterColorById(c.id);
      const acc = Math.round((this.classAcc.get(c.id) ?? 0) * 100);
      const row = document.createElement('div');
      row.className     = 'sm-grp-legend-row';
      row.dataset['id'] = String(c.id);
      row.innerHTML = `
        <div class="sm-sp-dot" style="background:${col};box-shadow:0 0 5px ${col};flex-shrink:0;"></div>
        <div class="sm-grp-legend-label">${c.label}</div>
        <div class="sm-grp-legend-count">${c.n_species}</div>
        <div class="sm-grp-cohesion-badge">${acc}%</div>`;
      row.style.cursor = 'pointer';
      row.addEventListener('click', () => this.selectGroup(c.id));
      el.appendChild(row);
    });
  }

  // ── Confidence slider ─────────────────────────────────────────────────────

  private buildSlider(): void {
    const slider = this.panel.querySelector<HTMLInputElement>('#sm-grp-conf-slider')!;
    slider.addEventListener('input', () => {
      const threshold = parseInt(slider.value) / 100;
      this.applyOpacity(threshold);
    });
  }

  private applyOpacity(threshold: number): void {
    this.enriched.forEach((e, i) => {
      if (e.confidence < threshold) {
        this.dotEls[i].setAttribute('opacity', '0.04');
      } else if (e.clusterId === this.selectedId) {
        this.dotEls[i].setAttribute('opacity', String(Math.max(0.55, this.baseOpacity[i])));
      } else {
        this.dotEls[i].setAttribute('opacity', String(this.baseOpacity[i]));
      }
    });
  }

  // ── Info tooltip ──────────────────────────────────────────────────────────

  private buildInfoTooltip(): void {
    const btn  = this.panel.querySelector<HTMLElement>('#sm-grp-info-btn')!;
    const tip  = this.panel.querySelector<HTMLElement>('#sm-grp-info-tooltip')!;
    btn.addEventListener('mouseenter', () => { tip.style.display = 'block'; });
    btn.addEventListener('mouseleave', () => { tip.style.display = 'none'; });
    btn.addEventListener('click', ev => {
      ev.stopPropagation();
      tip.style.display = tip.style.display === 'none' ? 'block' : 'none';
    });
    this.panel.addEventListener('click', () => { tip.style.display = 'none'; });
  }

  // ── Zoom controls ─────────────────────────────────────────────────────────

  private buildZoomControls(): void {
    const zoomBy = (factor: number) => {
      const cx = this.vb.x + this.vb.w / 2;
      const cy = this.vb.y + this.vb.h / 2;
      this.vb.x = cx - (this.vb.w * factor) / 2;
      this.vb.y = cy - (this.vb.h * factor) / 2;
      this.vb.w *= factor;
      this.vb.h *= factor;
      this.applyViewBox();
    };

    this.panel.querySelector('#sm-grp-zoom-in')!
      .addEventListener('click', () => zoomBy(0.75));
    this.panel.querySelector('#sm-grp-zoom-out')!
      .addEventListener('click', () => zoomBy(1.33));
    this.panel.querySelector('#sm-grp-zoom-reset')!
      .addEventListener('click', () => {
        this.vb = { x: 0, y: 0, w: 400, h: 370 };
        this.applyViewBox();
      });
  }

  // ── Fullscreen ────────────────────────────────────────────────────────────

  private buildFullscreen(): void {
    const btn  = this.panel.querySelector<HTMLElement>('#sm-grp-fullscreen')!;
    const wrap = this.panel.querySelector<HTMLElement>('#sm-grp-svg-wrap')!;
    btn.addEventListener('click', () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      } else {
        wrap.requestFullscreen().catch(() => {});
      }
    });
    document.addEventListener('fullscreenchange', () => {
      btn.textContent = document.fullscreenElement ? '✕' : '⛶';
    });
  }

  // ── Main map markers ─────────────────────────────────────────────────────

  private buildMainMapMarkers(): void {
    // Filter GPS outliers to study area bbox
    this.validPlots = this.plotSims.filter(p =>
      p.lat >= -16.45 && p.lat <= -15.3 &&
      p.lon >=  29.0  && p.lon <=  31.3
    );

    // Pre-compute dominant cluster per plot (argmax of sims) — stable across selections
    this.plotDomCluster = this.validPlots.map(p => {
      let best = 0;
      p.sims.forEach((s, i) => { if (s > p.sims[best]) best = i; });
      return best;
    });

    this.plotData = this.validPlots.map((p, i) => ({
      position: [p.lon, p.lat] as [number, number],
      color: hexToRgba(clusterColorById(this.plotDomCluster[i])),
      domId: this.plotDomCluster[i],
    }));

    this.layer = this.buildScatterLayer();
  }

  private buildScatterLayer(): ScatterplotLayer<PlotDatum> {
    const id = this.selectedId;
    return new ScatterplotLayer<PlotDatum>({
      id: 'grp-plots',
      data: this.plotData,
      getPosition: d => d.position,
      getFillColor: d => {
        const [r, g, b] = d.color;
        return [r, g, b, d.domId === id ? 230 : 46];
      },
      getRadius: d => d.domId === id ? 7 : 4,
      radiusUnits: 'pixels',
      stroked: true,
      getLineColor: [0, 0, 0, 80],
      lineWidthMinPixels: 0.5,
      updateTriggers: { getFillColor: id, getRadius: id },
    });
  }

  /** Highlight selected cluster's plots — others stay visible but dimmed. */
  private highlightMainMap(): void {
    this.layer = this.buildScatterLayer();
    this.map.addLayer(this.layer);
  }

  // ── Selection ─────────────────────────────────────────────────────────────

  private selectGroup(id: number): void {
    this.selectedId = id;
    const c   = this.clusters.find(x => x.id === id)!;
    const col = clusterColorById(id);
    const acc = Math.round((this.classAcc.get(id) ?? 0) * 100);

    // Slider threshold
    const pct = parseInt((this.panel.querySelector<HTMLInputElement>('#sm-grp-conf-slider')!).value) / 100;

    // Dot opacity: selected cluster bright, others dimmed
    this.enriched.forEach((e, i) => {
      if (e.confidence < pct) {
        this.dotEls[i].setAttribute('opacity', '0.04');
      } else if (e.clusterId === id) {
        this.dotEls[i].setAttribute('opacity', String(Math.max(0.55, this.baseOpacity[i])));
      } else {
        this.dotEls[i].setAttribute('opacity', String(Math.max(0.04, this.baseOpacity[i] * 0.25)));
      }
    });

    // Legend highlight
    this.panel.querySelectorAll<HTMLElement>('.sm-grp-legend-row').forEach(r => {
      r.classList.toggle('active', r.dataset['id'] === String(id));
    });

    // Detail panel
    this.panel.querySelector<HTMLElement>('#sm-grp-detail-name')!.textContent  = c.label;
    this.panel.querySelector<HTMLElement>('#sm-grp-detail-name')!.style.color  = col;
    this.panel.querySelector<HTMLElement>('#sm-grp-detail-meta')!.innerHTML =
      `${c.n_species} species &nbsp;·&nbsp; <span style="color:var(--c-green)">${acc}% aligned with model</span>`;

    // Species list sorted high→low confidence
    const listEl  = this.panel.querySelector<HTMLElement>('#sm-grp-sp-list')!;
    listEl.innerHTML = '';
    const groupSp = this.enriched
      .filter(e => e.clusterId === id)
      .sort((a, b) => b.confidence - a.confidence);

    const maxC = Math.max(...groupSp.map(s => s.count), 1);
    groupSp.slice(0, 12).forEach(sp => {
      const barW    = Math.round((sp.count / maxC) * 100);
      const confPct = Math.round(sp.confidence * 100);
      const row     = document.createElement('div');
      row.className = 'sm-grp-sp-row';
      row.innerHTML = `
        <div class="sm-sp-dot" style="background:${col};box-shadow:0 0 3px ${col};"></div>
        <div class="sm-sp-name">${sp.name}</div>
        <div class="sm-sp-count">${sp.count}</div>
        <div class="sm-sim-bar">
          <div class="sm-sim-fill" style="width:${barW}%;background:${col};"></div>
        </div>
        <div class="sm-grp-conf-badge">${confPct}%</div>`;
      listEl.appendChild(row);
    });
    if (groupSp.length > 12) {
      const more = document.createElement('div');
      more.style.cssText = 'font-size:9px;color:var(--c-grey);padding:4px 0;letter-spacing:1px;';
      more.textContent = `+ ${groupSp.length - 12} more`;
      listEl.appendChild(more);
    }

    this.radial.show(c.centroid_embed);
    this.highlightMainMap();
  }
}
